import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { getTenantSubdomain } from "./useTenantFromSubdomain";

export const DEFAULT_THEME = {
  primaryColor: "#2a3472",
  secondaryColor: "#898C9C",
  tertiaryColor: "#B3B3B3",
  sidebarColor: "#BABCCB",
  userbarColor: "#f0f6f8",
  surfaceColor: "#ffffff",
  fontColor: "#000000",
  primaryFontColor: "#000000",
  secondaryFontColor: "#6B7280",
  logoUrl: null,
  appName: "Canvassing App",
  dispositionColors: {},
};

const STORAGE_KEY = "tenantTheme";
const REFRESH_MIN_GAP_MS = 30 * 1000;

// Tailwind's opacity-modifier utilities (e.g. border-canvas-primary/50) require
// the underlying CSS variable to hold "R G B" channels rather than a hex
// string, so it can splice in the alpha value itself (see tailwind.config.js).
const hexToRgbChannels = (hex) => {
  const match = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex || "");
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)} ${parseInt(g, 16)} ${parseInt(b, 16)}`;
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  const setColor = (varName, hex) => {
    const rgb = hexToRgbChannels(hex);
    if (rgb) root.style.setProperty(varName, rgb);
  };
  setColor("--canvas-primary-rgb", theme.primaryColor);
  setColor("--canvas-secondery-rgb", theme.secondaryColor);
  setColor("--canvas-tersery-rgb", theme.tertiaryColor);
  setColor("--canvas-sidebar-rgb", theme.sidebarColor);
  setColor("--canvas-userbar-rgb", theme.userbarColor);
  setColor("--canvas-surface-rgb", theme.surfaceColor);
  setColor("--canvas-font-rgb", theme.fontColor);
  setColor("--canvas-primary-font-rgb", theme.primaryFontColor);
  setColor("--canvas-secondary-font-rgb", theme.secondaryFontColor);

  const appName = theme.appName || DEFAULT_THEME.appName;
  document.title = appName;
  const descriptionTag = document.querySelector('meta[name="description"]');
  if (descriptionTag) descriptionTag.setAttribute("content", appName);
  const appleTitleTag = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitleTag) appleTitleTag.setAttribute("content", appName);
};

const readCachedTheme = () => {
  const cached = localStorage.getItem(STORAGE_KEY);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch (error) {
    console.error("Error parsing cached tenant theme:", error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const useTenantTheme = () => {
  const { REACT_APP_BASE_URL } = process.env;
  const [theme, setTheme] = useState(() => {
    const cached = readCachedTheme();
    return cached ? { ...DEFAULT_THEME, ...cached } : DEFAULT_THEME;
  });

  useEffect(() => {
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastFetchAtRef = useRef(0);

  const fetchTheme = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    lastFetchAtRef.current = Date.now();
    try {
      const subdomain = getTenantSubdomain();
      const response = await axios.get(`${REACT_APP_BASE_URL}/tenant/theme`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(subdomain ? { "X-Tenant-Subdomain": subdomain } : {}),
        },
      });
      const fetchedTheme = { ...DEFAULT_THEME, ...response.data };
      setTheme(fetchedTheme);
      applyTheme(fetchedTheme);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fetchedTheme));
    } catch (error) {
      lastFetchAtRef.current = 0;
      console.error("Error fetching tenant theme:", error);
    }
  }, [REACT_APP_BASE_URL]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  // An installed PWA is resumed, not reloaded, so without this it keeps the colors it cached until the next cold start.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFetchAtRef.current < REFRESH_MIN_GAP_MS) return;
      fetchTheme();
    };

    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [fetchTheme]);

  // Pre-login path (Login.js): no JWT exists yet, so the tenant is identified
  // by subdomain instead. Unlike fetchTheme, this deliberately does NOT
  // persist to the shared STORAGE_KEY cache — an unauthenticated visitor
  // hasn't proven which tenant they belong to, so their guess-by-URL theme
  // must not leak into localStorage and get picked up by a different,
  // possibly-different-tenant user signing in on the same browser afterward.
  const fetchThemeBySubdomain = useCallback(async (subdomain) => {
    try {
      const response = await axios.get(`${REACT_APP_BASE_URL}/tenant/theme/by-subdomain/${subdomain}`);
      const fetchedTheme = { ...DEFAULT_THEME, ...response.data };
      setTheme(fetchedTheme);
      applyTheme(fetchedTheme);
    } catch (error) {
      console.error("Error fetching theme by subdomain:", error);
    }
  }, [REACT_APP_BASE_URL]);

  const updateThemeLocally = useCallback((nextTheme) => {
    const merged = { ...DEFAULT_THEME, ...nextTheme };
    setTheme(merged);
    applyTheme(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }, []);

  // Called on logout — without this, the in-memory theme state (and thus the
  // applied CSS variables) keeps showing the just-logged-out tenant's colors
  // until a fetch resolves for whoever logs in next on this browser, since
  // logout is a client-side navigate, not a full reload that would otherwise
  // remount ThemeProvider and reset this state naturally.
  const resetTheme = useCallback(() => {
    setTheme(DEFAULT_THEME);
    applyTheme(DEFAULT_THEME);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return { theme, fetchTheme, fetchThemeBySubdomain, updateThemeLocally, resetTheme };
};

// logoUrl from the API is a server-relative path (e.g. /uploads/logos/x.png);
// REACT_APP_BASE_URL points at .../api, so strip that suffix to get the
// server origin for static file URLs.
export const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  const { REACT_APP_BASE_URL } = process.env;
  return `${(REACT_APP_BASE_URL || "").replace(/\/api$/, "")}${logoUrl}`;
};

export default useTenantTheme;
